(function () {
  const SELECTOR = ".js-interactive-heading";
  // Fill window, as a share of the viewport height measured from its top: the
  // reveal starts with the heading top at FILL_START and is complete once it
  // reaches FILL_END, so the text is solid while still well inside the viewport.
  const FILL_START = 1;
  const FILL_END = 0.3;

  class InteractiveHeading {
    constructor(el) {
      this.el = el;
      this.originalText = el.textContent.trim();
      this.chars = [];
      this.fillOrder = [];
      this.filledCount = -1;
      this.raf = null;

      this._splitChars();
      this._computeFillOrder();

      this.onScroll = this._requestUpdate.bind(this);
      this.onResize = this._requestReflow.bind(this);
      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize, { passive: true });

      this._update();
    }

    destroy() {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
      if (this.raf) cancelAnimationFrame(this.raf);
      this.el.classList.remove("interactive-heading--ready");
      this.el.removeAttribute("aria-label");
      this.el.textContent = this.originalText;
    }

    // characters are wrapped per word so a word still wraps as one unit
    _splitChars() {
      const wrapper = document.createElement("span");
      wrapper.className = "interactive-heading__words";
      wrapper.setAttribute("aria-hidden", "true");

      const words = this.originalText.split(/\s+/).filter(Boolean);
      words.forEach((word, i) => {
        const wordSpan = document.createElement("span");
        wordSpan.className = "interactive-heading__word";

        Array.from(word).forEach((char) => {
          const charSpan = document.createElement("span");
          charSpan.className = "interactive-heading__char";
          charSpan.textContent = char;
          wordSpan.appendChild(charSpan);
        });

        wrapper.appendChild(wordSpan);
        if (i < words.length - 1) {
          wrapper.appendChild(document.createTextNode(" "));
        }
      });

      this.el.setAttribute("aria-label", this.originalText);
      this.el.textContent = "";
      this.el.appendChild(wrapper);
      this.chars = Array.from(
        wrapper.querySelectorAll(".interactive-heading__char")
      );
      this.el.classList.add("interactive-heading--ready");
    }

    // groups characters into their rendered lines, then orders each line
    // start-to-end for the current reading direction. Measuring the actual
    // rendered position (rather than trusting dom order + direction) keeps
    // the reveal rtl-correct even when the heading text itself isn't a bidi
    // script (e.g. latin text under dir="rtl"), where the browser doesn't
    // reorder the words on its own.
    _computeFillOrder() {
      if (!this.chars.length) return;

      const isRTL = getComputedStyle(this.el).direction === "rtl";
      const lines = [];

      this.chars.forEach((char) => {
        const rect = char.getBoundingClientRect();
        let line = lines.find(
          (candidate) => Math.abs(candidate.top - rect.top) < rect.height / 2
        );
        if (!line) {
          line = { top: rect.top, items: [] };
          lines.push(line);
        }
        line.items.push({ char, left: rect.left });
      });

      lines.sort((a, b) => a.top - b.top);
      this.fillOrder = lines.flatMap((line) => {
        line.items.sort((a, b) => (isRTL ? b.left - a.left : a.left - b.left));
        return line.items.map((item) => item.char);
      });
    }

    /* 0 = heading top at the FILL_START line, 1 = heading top at FILL_END */
    _progress() {
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      if (viewportHeight <= 0) return 1;

      const doc = document.documentElement;
      const atBottom =
        window.scrollY + viewportHeight >= doc.scrollHeight - 1;
      if (atBottom) return 1;

      const rect = this.el.getBoundingClientRect();
      const travelled = viewportHeight * FILL_START - rect.top;
      const distance = viewportHeight * (FILL_START - FILL_END);
      return Math.min(Math.max(travelled / distance, 0), 1);
    }

    _requestUpdate() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this._update();
      });
    }

    _requestReflow() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this._computeFillOrder();
        this.filledCount = -1;
        this._update();
      });
    }

    _update() {
      if (!this.fillOrder.length) return;

      const count = Math.round(this._progress() * this.fillOrder.length);
      const previous = this.filledCount;
      if (count === previous) return;
      this.filledCount = count;

      // only the characters between the old and the new boundary change state
      const from = previous < 0 ? 0 : Math.min(previous, count);
      const to = previous < 0 ? this.fillOrder.length : Math.max(previous, count);
      for (let i = from; i < to; i++) {
        this.fillOrder[i].classList.toggle("is-filled", i < count);
      }
    }
  }

  function initAll(root) {
    if (!root) return;

    const headings = root.querySelectorAll
      ? root.querySelectorAll(SELECTOR)
      : [];

    headings.forEach((el) => {
      if (el._interactiveHeadingInstance) return;
      el._interactiveHeadingInstance = new InteractiveHeading(el);
    });
  }

  function destroyAll(root) {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el._interactiveHeadingInstance) return;
      el._interactiveHeadingInstance.destroy();
      el._interactiveHeadingInstance = null;
    });
  }

  initAll(document);

  if (!window.__themeInteractiveHeadingEvents) {
    window.__themeInteractiveHeadingEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initAll(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      destroyAll(event.target);
    });
  }
})();
